import {
  ColumnOrganizerType,
  ColumnType,
  ColumnWidth,
  Pinned,
  SortDirection,
  TableDefinition,
} from '@vendasta/va-filter2-table';
import { AccountsAuxiliaryDataTableFilterService } from './auxiliary-data-accounts-table-filters.service';
import { AccountsCustomFieldsData, AccountsCustomFieldsTableColumns } from './auxiliary-data-accounts-table.data-model';
import { AccountNameColumnComponent } from './column-components/account-name-column/account-name-column.component';
import { CityColumnComponent } from './column-components/city-column/city-column.component';
import { LastUpdateDateColumnComponent } from './column-components/last-update-date-column/last-update-date-column.component';
import { PrimaryContactColumnComponent } from './column-components/primary-contact-column/primary-contact-column.component';
import { StateColumnComponent } from './column-components/state-column/state-column.component';
import { AuxiliaryDataFieldSchemaInterface } from '@vendasta/account-group/lib/_internal/interfaces/fields.interface';
import { CustomFieldColumnComponent } from './column-components/custom-field-column/custom-field-column.component';
import { cloneDeep } from 'lodash';

export interface AccountSelectorRowEvent {
  row: AccountsCustomFieldsData;
}

const ACCOUNTS_AUXILIARY_DATA_TABLE_DEF: TableDefinition = {
  id: 'accounts-custom-fields',
  columns: [
    {
      id: AccountsCustomFieldsTableColumns.ACCOUNT_NAME,
      displayName: 'Account Name',
      cellComponent: AccountNameColumnComponent,
      pinned: Pinned.PINNED_LEFT,
      type: ColumnType.COLUMN_TYPE_STRING,
    },
    {
      id: AccountsCustomFieldsTableColumns.CITY,
      displayName: 'City',
      cellComponent: CityColumnComponent,
      pinned: Pinned.PINNED_UNSET,
      type: ColumnType.COLUMN_TYPE_STRING,
    },
    {
      id: AccountsCustomFieldsTableColumns.STATE,
      displayName: 'State',
      cellComponent: StateColumnComponent,
      pinned: Pinned.PINNED_UNSET,
      type: ColumnType.COLUMN_TYPE_STRING,
      width: ColumnWidth.WIDTH_NARROW,
    },
    {
      id: AccountsCustomFieldsTableColumns.LAST_UPDATE_DATE,
      displayName: 'Last Updated',
      cellComponent: LastUpdateDateColumnComponent,
      sortOptions: {
        sortable: false,
        initialSortDirection: SortDirection.SORT_DIRECTION_DESCENDING,
      },
      pinned: Pinned.PINNED_UNSET,
      type: ColumnType.COLUMN_TYPE_DATE,
    },
    {
      id: AccountsCustomFieldsTableColumns.PRIMARY_CONTACT,
      displayName: 'Primary Contact',
      cellComponent: PrimaryContactColumnComponent,
      pinned: Pinned.PINNED_UNSET,
      type: ColumnType.COLUMN_TYPE_STRING,
    },
  ],
  columnOrganizerType: ColumnOrganizerType.ADVANCED_ORGANIZER,
  options: {
    selectableRows: false,
    pageSizeOptions: [5, 10, 25, 50],
    defaultPageSize: 25,
    bulkActions: [],
  },
  labels: {
    noResults: 'No results',
    cancel: 'Cancel',
    none: 'None',
    update: 'Update',
    sortBy: 'Sort By',
    thenSortBy: 'Then Sort By',
    addAnotherSortColumn: 'Add another column to sort on',
    chooseColumnToSortBy: 'Choose a column to sort on',
    columnSelector: {
      buttonText: 'Columns',
    },
    columnSorting: {
      date: {
        ascending: 'Earliest first',
        descending: 'Latest first',
      },
      number: {
        ascending: '1 to 9',
        descending: '9 to 1',
      },
      string: {
        ascending: 'A to Z',
        descending: 'Z to A',
      },
      default: {
        ascending: 'ascending',
        descending: 'descending',
      },
    },
    export: {
      buttonText: 'Export',
      buttonTooltipBeforeCount: 'Export',
      buttonTooltipAfterCount: 'items',
    },
  },
};

export const tableDefinitionFactory = (
  filterService: AccountsAuxiliaryDataTableFilterService,
  fieldSchemas: AuxiliaryDataFieldSchemaInterface[],
  enableAddToListButton: boolean,
) => {
  // every time the factory is called
  // re-clone definition, to clean up custom columns
  const tableDefinition = cloneDeep(ACCOUNTS_AUXILIARY_DATA_TABLE_DEF);
  tableDefinition.filters = filterService.filters;
  if (fieldSchemas?.length > 0) {
    for (const schema of fieldSchemas) {
      tableDefinition.columns.push({
        id: schema.fieldId,
        displayName: schema.fieldName,
        cellComponent: CustomFieldColumnComponent,
        pinned: Pinned.PINNED_UNSET,
        type: ColumnType.COLUMN_TYPE_UNSET,
      });
    }
  }
  if (enableAddToListButton) {
    tableDefinition.options.bulkActions.push({
      actionButtonText: 'Add To List',
      actionButtonMaterialIcon: 'playlist_add',
      actionId: 'add-accounts-to-list',
      description: 'Export results to an Account List',
    });
  }
  return tableDefinition;
};
